// The header both layouts share: who and where, then what to do with a pod.
//
// Left, the identity block — the operator's face, the station (or "Production"
// for a leader on the floor) and a line under it saying which lines are in
// view. Right, Find — and on a tablet Scan beside it, where there is width for
// a labelled button. A phone puts Scan in the middle of the tab bar instead,
// under the thumb rather than up by the title, so the header keeps only Find.
// On a tablet the scope chips and the score sit between the two ends; on a
// phone they drop to a row under the header and into the flowline card.

import type { ReactNode } from 'react';
import { employeeAvatarUrl } from '@/api/client';
import type { ScoreVM } from '@/api/types';
import { Icon } from '@/components/ui/Icon';
import { AvatarTile } from '@/components/ui/misc';
import { StatChip, type Tone } from '@/components/home/Score';
import { ScopeChips, type ScopeOption } from '@/components/home/ScopeChips';
import { useT } from '@/i18n/useT';
import { paceLabel, paceTone } from '@/lib/pace';
import { useStore } from '@/store';

export function AppHeader({
  title,
  sub,
  tablet,
  scopeOptions,
  scopeValue,
  onScope,
  score,
  onIdentity,
  onScan,
  onFind,
  trailing,
}: {
  title: string;
  sub: string;
  tablet: boolean;
  scopeOptions: ScopeOption[];
  scopeValue: number | null;
  onScope: (flowlineId: number | null) => void;
  /** The headline score for the tablet header. Null hides it. */
  score: ScoreVM | null;
  onIdentity: () => void;
  onScan: () => void;
  onFind: () => void;
  /** Status chrome — the offline pill. */
  trailing?: ReactNode;
}) {
  const t = useT();
  const operator = useStore((s) => s.operator);
  const scanning = useStore((s) => s.scanning);

  return (
    <>
      <header
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--line)',
          minHeight: 60,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          className="press"
          onClick={onIdentity}
          aria-label={t.settings}
          style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, minHeight: 44, flex: 1, textAlign: 'left' }}
        >
          <AvatarTile
            name={operator?.name ?? '?'}
            size={36}
            {...(operator ? { photoUrl: employeeAvatarUrl(operator.id) } : {})}
          />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: 'block',
                font: "700 16px 'Space Grotesk',sans-serif",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </span>
            <span
              style={{
                display: 'block',
                font: '500 11px Manrope,sans-serif',
                color: 'var(--sub)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {sub} ▾
            </span>
          </span>
        </button>

        {tablet && scopeOptions.length > 1 && (
          <ScopeChips variant="pill" options={scopeOptions} value={scopeValue} onChange={onScope} />
        )}

        {tablet && score && <HeaderScore score={score} />}

        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {trailing}
          {tablet && (
          <button
            type="button"
            className="press"
            onClick={onScan}
            style={{
              minHeight: 40,
              borderRadius: 12,
              background: 'var(--ink)',
              color: 'var(--bg)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 14px',
              font: '700 12.5px Manrope,sans-serif',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Icon name="barcode" size={16} strokeWidth={2} />
            <span aria-live="polite">{scanning ? t.scanning : t.scanPod}</span>
            {scanning && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: 2,
                  background: 'var(--accent)',
                  boxShadow: '0 0 10px var(--accent)',
                  animation: 'ipSweep 1s linear infinite',
                }}
              />
            )}
          </button>
          )}
          <button
            type="button"
            className="press"
            onClick={onFind}
            aria-label={t.findPod}
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--ink)',
            }}
          >
            <Icon name="search" size={17} />
          </button>
        </span>
      </header>

      {!tablet && scopeOptions.length > 1 && (
        <div style={{ flex: 'none', padding: '8px 16px 0' }}>
          <ScopeChips variant="row" options={scopeOptions} value={scopeValue} onChange={onScope} />
        </div>
      )}
    </>
  );
}

/** The score, sized to be read from a metre away — the point of the wider screen. */
function HeaderScore({ score }: { score: ScoreVM }) {
  const t = useT();
  const tone = paceTone(score.delta);
  const chip: Tone = tone === 'pos' ? 'pos' : tone === 'danger' ? 'danger' : 'neutral';
  return (
    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span className="numeric" style={{ font: "700 28px/1 'Space Grotesk',sans-serif" }}>
        {score.done}
        {score.target != null && <span style={{ color: 'var(--sub)', fontSize: 17 }}>/{score.target}</span>}
      </span>
      <StatChip tone={chip} style={{ fontSize: 12, padding: '4px 10px' }}>
        {paceWord(score, t)}
      </StatChip>
    </span>
  );
}

/** "Ahead +2", "Behind −1", "on pace", or why there is no pace. */
export function paceWord(score: ScoreVM, t: ReturnType<typeof useT>): string {
  if (score.delta == null) return score.target == null ? t.noTargetT : score.onShift ? '—' : t.offShiftT;
  if (score.delta === 0) return t.onPaceT;
  return `${score.delta > 0 ? t.aheadT : t.behindT} ${paceLabel(score.delta).replace('-', '−')}`;
}
